import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Paper, IconButton, Chip, Grid, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon, ChevronRight as NextIcon,
} from '@mui/icons-material';
import {
  format, startOfMonth, endOfMonth, startOfWeek, addDays, isSameDay,
  isSameMonth, addMonths, subMonths, parseISO, isWithinInterval,
} from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../services/taskService';
import resourceService from '../services/resourceService';

const priorityColors: Record<string, string> = {
  '1': '#10B981', '2': '#3B82F6', '3': '#F59E0B', '4': '#EF4444',
};

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState<any[]>([]);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showType, setShowType] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, [currentDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const start = startOfMonth(currentDate).toISOString();
      const end = endOfMonth(currentDate).toISOString();

      const [tasksData, timelineData] = await Promise.all([
        taskService.getAllTasks(),
        resourceService.getTimeline(start, end).catch(() => []),
      ]);

      setTasks(tasksData as any[]);

      // Flatten all bookings from timeline
      const allBookings = (timelineData || []).flatMap((r: any) =>
        (r.bookings || []).map((b: any) => ({ ...b, resourceColor: r.color }))
      );
      setBookings(allBookings);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build calendar grid
  const monthStart = startOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const cells = Array.from({ length: 42 }, (_, i) => addDays(calendarStart, i));

  const getTasksForDay = (day: Date) => {
    return tasks.filter(t => {
      if (!t.dueDate) return false;
      return isSameDay(parseISO(t.dueDate), day);
    });
  };

  const getBookingsForDay = (day: Date) => {
    return bookings.filter(b => {
      const bStart = parseISO(b.startTime);
      const bEnd = parseISO(b.endTime);
      return isSameDay(day, bStart) || isSameDay(day, bEnd) ||
        isWithinInterval(day, { start: bStart, end: bEnd });
    });
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => setCurrentDate(subMonths(currentDate, 1))}><PrevIcon /></IconButton>
          <Typography variant="h5" fontWeight="bold" sx={{ minWidth: 200, textAlign: 'center' }}>
            {format(currentDate, 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={() => setCurrentDate(addMonths(currentDate, 1))}><NextIcon /></IconButton>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Show</InputLabel>
          <Select value={showType} label="Show" onChange={e => setShowType(e.target.value)}>
            <MenuItem value="all">Tasks & Resources</MenuItem>
            <MenuItem value="tasks">Tasks Only</MenuItem>
            <MenuItem value="resources">Resources Only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Calendar Grid */}
      <Paper sx={{ overflow: 'hidden' }}>
        {/* Day headers */}
        <Grid container>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <Grid item xs={12 / 7} key={d} sx={{
              p: 1, textAlign: 'center', bgcolor: 'grey.100',
              borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
            }}>
              <Typography variant="caption" fontWeight={700}>{d}</Typography>
            </Grid>
          ))}
        </Grid>

        {/* Cells */}
        <Grid container>
          {cells.map(day => {
            const dayTasks = showType !== 'resources' ? getTasksForDay(day) : [];
            const dayBookings = showType !== 'tasks' ? getBookingsForDay(day) : [];
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());

            return (
              <Grid item xs={12 / 7} key={day.toISOString()} sx={{
                minHeight: 100, borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                p: 0.5, opacity: isCurrentMonth ? 1 : 0.3,
                bgcolor: isToday ? 'primary.50' : 'inherit',
              }}>
                <Typography variant="caption" fontWeight={isToday ? 700 : 400}
                  color={isToday ? 'primary' : 'inherit'} sx={{ display: 'block', mb: 0.25 }}>
                  {format(day, 'd')}
                </Typography>

                {/* Tasks */}
                {dayTasks.slice(0, 2).map((t: any) => (
                  <Tooltip key={t.id} title={`${t.projectKey || ''} - ${t.title}`}>
                    <Box sx={{
                      bgcolor: priorityColors[String(t.priority)] || '#6B7280',
                      color: '#fff', borderRadius: 0.5, px: 0.5, mb: 0.25,
                      fontSize: '0.6rem', overflow: 'hidden', whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis', cursor: 'pointer',
                    }} onClick={() => navigate(`/tasks/${t.id}`)}>
                      {t.title}
                    </Box>
                  </Tooltip>
                ))}

                {/* Bookings */}
                {dayBookings.slice(0, 2).map((b: any) => (
                  <Tooltip key={b.id} title={`${b.resourceName}: ${b.title}`}>
                    <Box sx={{
                      bgcolor: b.resourceColor || '#8B5CF6',
                      color: '#fff', borderRadius: 0.5, px: 0.5, mb: 0.25,
                      fontSize: '0.6rem', overflow: 'hidden', whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis', cursor: 'pointer',
                      border: '1px dashed rgba(255,255,255,0.5)',
                    }} onClick={() => navigate(`/resources/${b.resourceId}/calendar`)}>
                      {b.resourceName}: {b.title}
                    </Box>
                  </Tooltip>
                ))}

                {(dayTasks.length + dayBookings.length) > 4 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                    +{dayTasks.length + dayBookings.length - 4} more
                  </Typography>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 1.5, mt: 2, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight={600}>Tasks:</Typography>
        {[['Low', '#10B981'], ['Medium', '#3B82F6'], ['High', '#F59E0B'], ['Urgent', '#EF4444']].map(([label, color]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: 0.5 }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
        <Typography variant="caption" fontWeight={600} sx={{ ml: 2 }}>Resources:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, bgcolor: '#8B5CF6', borderRadius: 0.5, border: '1px dashed #fff' }} />
          <Typography variant="caption">Bookings</Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default CalendarPage;
